<template>
    <div>
        <div ref="stockStickySentinel" class="sticky-sentinel"></div>
        <InformationDetailStickyHeader
            ref="stickyHeaderRef"
            :show="showStockSticky"
            :quotes="quotes"
            :current-index="currentIndex"
            :sticky-top-css="isApp ? stickyTopCss : stickyTopCssIsNotApp"
            :theme="theme"
            :mkt-color="mktColor"
            :is-app="isApp"
            :is-t-h-s-app="isTHSApp"
            @go-quote-detail="onHeaderClick"
            @attention="onAttentionSticky"
        />
        <section class="stock-box" v-if="quotes && quotes.length > 0">
            <section class="stock-list">
                <StockListItem
                    v-for="(item, index) in visibleQuotes"
                    :key="`original-${item.code || item.rawSymbol || index}`"
                    :item="item"
                    :theme="theme"
                    :mkt-color="mktColor"
                    :is-app="isApp"
                    :is-t-h-s-app="isTHSApp"
                    :show-night="!!isShowStockNight(item)"
                    @click="onItemClick(item)"
                    @attention="onAttention(index)"
                />
            </section>
        </section>
    </div>
</template>

<script>
import { computed, toRefs, ref } from 'vue'
import InformationDetailStickyHeader from './InformationDetailStickyHeader.vue'
import StockListItem from './StockListItem.vue'
import { useStickyHeader } from '../composables/useStickyHeader'

export default {
    name: 'StockQuotes',
    components: {
        InformationDetailStickyHeader,
        StockListItem
    },
    emits: ['go-quote-detail', 'attention'],
    props: {
        quotes: {
            type: Array,
            default: () => []
        },
        onGoQuoteDetail: {
            type: Function,
            default: null
        },
        theme: {
            type: String,
            default: 'white'
        },
        mktColor: {
            type: Object,
            default: () => ({})
        },
        isApp: {
            type: Boolean,
            default: true
        },
        isTHSApp: {
            type: Function, // Passed as function in original
            default: () => false
        }
    },
    setup(props, { emit }) {
        const { isApp } = toRefs(props)
        const stickyHeaderRef = ref(null)
        const currentIndex = ref(0)
        
        // Sticky Header Logic
        const { showStockSticky, stockStickySentinel, stickyTopCss, stickyTopCssIsNotApp } = useStickyHeader(isApp)

        // Computed
        const visibleQuotes = computed(() => {
            return props.quotes ? props.quotes.slice(0, 3) : []
        })

        const firstQuote = computed(() => {
            if (!props.quotes || props.quotes.length === 0) return null
            const index = currentIndex.value % props.quotes.length
            return props.quotes[index]
        })

        // Methods
        const isShowStockNight = (item) => {
            return item.nightTrading && item.nightQuote && item.nightQuote.price
        }

        const nextCarousel = () => {
            if (!props.quotes || props.quotes.length === 0) return
            currentIndex.value = (currentIndex.value + 1) % props.quotes.length
        }

        const onAttention = (index) => {
            emit('attention', index)
        }

        const onAttentionSticky = (item) => {
            if (item && item.code) {
                const index = props.quotes.findIndex(q => q.code === item.code)
                if (index >= 0) {
                    emit('attention', index)
                    return
                }
            }
            if (!props.quotes || props.quotes.length === 0) return
            const index = currentIndex.value % props.quotes.length
            emit('attention', index)
        }

        const onItemClick = (item) => {
            emit('go-quote-detail', item)
            try {
                if (props.onGoQuoteDetail) props.onGoQuoteDetail(item)
            } catch (e) {}
        }

        const onHeaderClick = (item) => {
            const quote = (item && item.code) ? item : firstQuote.value
            if (!quote) return
            emit('go-quote-detail', quote)
            try {
                if (props.onGoQuoteDetail) props.onGoQuoteDetail(quote)
            } catch (e) {}
        }

        return {
            stickyHeaderRef,
            showStockSticky,
            stockStickySentinel,
            stickyTopCss,
            stickyTopCssIsNotApp,
            nextCarousel,
            currentIndex,
            visibleQuotes,
            firstQuote,
            isShowStockNight,
            onAttention,
            onAttentionSticky,
            onItemClick,
            onHeaderClick
        }
    }
}
</script>

<style lang="less" scoped>
@import url('../css/mixins.less');

.stock-box {
    position: relative;
}

.stock-list {
    padding: 8px 0;
    position: relative;
    overflow: hidden;
    border-radius: 8px;
    display: block;
}

.stock-carousel-wrapper {
    position: relative;
    transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    will-change: transform;
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
    transform-style: preserve-3d;
    display: block;
    transform-origin: top center;
}

.sticky-sentinel {
    width: 100%;
    height: 1px;
}
</style>
